 
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AuthService } from '../services/auth/auth.service';
import { Router, ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar'; // Import MatSnackBar
import { Observable } from 'rxjs';
import { HeaderComponent } from './header/header.component';
import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { ConfigurationClientService } from '../services/client/configuration-client.service';

@Component({
  selector: 'app-login',
  imports: [CommonModule, ReactiveFormsModule, HeaderComponent],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  animations: [
    trigger('fade', [
      state('void', style({ opacity: 0 })),
      transition(':enter', [animate('600ms ease-in', style({ opacity: 1 }))]),
    ]),
  ],
})
export class LoginComponent implements OnInit {
  loginForm!: FormGroup;
  isLoading = false;
  showPassword = false;
  errorMessage: string | null = null;
  returnUrl: string | null;
  isAuthenticated$!: Observable<boolean>;
  defaultLandingRoute = '/chat';

  allowCreateAccount = true;
  showSubheading = true;
  showSocialLogin = true;
  showGoogleLogin = true;
  showAppleLogin = false;
  showFacebookLogin = false;
  showForgotPassword = true;

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private snackBar: MatSnackBar,
    private config: ConfigurationClientService
  ) {
    this.isAuthenticated$ = this.authService.isAuthenticatedWait();
    this.returnUrl = config.landingRoute;
  }

  ngOnInit(): void {
    // init form
    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', Validators.required],
      rememberMe: [false],
    });

    // Get the return URL from the route parameters or default to '/'
    this.returnUrl =
      this.route.snapshot.queryParams['returnUrl'] || this.returnUrl;

    // Check if user is already logged in
    this.isAuthenticated$.subscribe((isAuth) => {
      if (isAuth) {
        this.router.navigate([this.returnUrl]); // Redirect to the return URL if authenticated
      }
    });
  }

  async onSubmit(): Promise<void> {
    if (this.loginForm.valid) {
      this.isLoading = true;
      try {
        const { email, password } = this.loginForm.value;
        await this.authService.signIn(email, password);
        this.router.navigate([this.returnUrl]);
      } catch (error: Error | any) {
        console.error('Login error:', error?.message);
        this.showMessage('Invalid email or password: ' + error?.message, 'top');
        // Handle error (show message to user)
        this.handleAuthError(error);
      } finally {
        this.isLoading = false;
      }
    }
  }

  async signInWithProvider(
    provider: 'google' | 'apple' | 'facebook'
  ): Promise<void> {
    this.isLoading = true;
    try {
      await this.authService.signInWithProvider(provider);
      this.router.navigate([this.returnUrl]);
    } catch (error: Error | any) {
      console.error(`${provider} sign-in error:`, error);
      this.handleAuthError(error);

      // Handle error
    } finally {
      this.isLoading = false;
    }
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  handleAuthError(error: Error | any): void {
    console.error('Authentication error:', error);
    this.errorMessage = error?.message;
    // Handle error
  }

  showMessage(
    message = 'Coming Soon!',
    verticalPosition: 'top' | 'bottom' = 'top'
  ) {
    this.snackBar.open(message, 'Close', {
      duration: 2000,
      horizontalPosition: 'center',
      verticalPosition: verticalPosition,
    });
  }
}
